import axios from "axios";

export const STANDARD_TEMPLATE_KIND = "Data Report";
export const PDF_TEMPLATE_KIND = "PDF Report";

const DEFAULT_PAGE_SIZE = 100;

const build_page_numbers = (total_pages) =>
  Array.from({ length: Math.max(total_pages - 1, 0) }, (_, index) => index + 2);

const build_standard_template_record = (template, component) => ({
  id: template.id,
  name: template.name,
  template_kind: STANDARD_TEMPLATE_KIND,
  category: component.translate_label(
    template.report_type?.display_name || "-"
  ),
  scope: component.translate_label(template.scope_type?.display_name || "-"),
  entity_types: component.format_entity_types(template),
  setup: component.translate_label(
    `${template.columns?.length || 0} columns / ${component.get_question_count(
      template
    )} questions`
  ),
  updated: component.format_date(template.date_modified),
  template,
});

const build_pdf_template_record = (template, component) => ({
  id: template.id,
  name: template.name,
  template_kind: PDF_TEMPLATE_KIND,
  category: component.translate_label(
    component.format_pdf_generation_mode(template.generation_mode)
  ),
  scope: component.translate_label("Single-entity PDF"),
  entity_types: component.format_pdf_entity_types(template),
  setup: component.translate_label(
    `${template.sections?.length || 0} sections`
  ),
  updated: component.format_date(template.date_modified),
  template,
});

const sort_by_last_modified = (left_row, right_row) =>
  new Date(right_row.template?.date_modified || 0) -
  new Date(left_row.template?.date_modified || 0);

export const build_combined_template_records = (component) => {
  const standard_templates = (component.standard_templates || []).map((template) =>
    build_standard_template_record(template, component)
  );
  const pdf_templates = (component.pdf_templates || []).map((template) =>
    build_pdf_template_record(template, component)
  );
  return [...standard_templates, ...pdf_templates].sort(sort_by_last_modified);
};

export const build_table_row = (template_record) => ({
  id: template_record.id,
  template_name: template_record.name,
  template_type: template_record.template_kind,
  category: template_record.category,
  scope: template_record.scope,
  entity_types: template_record.entity_types,
  setup: template_record.setup,
  updated: template_record.updated,
});

export const paginate_template_records = (
  template_records,
  page_number,
  page_size
) => {
  const start_index = (page_number - 1) * page_size;
  return template_records.slice(start_index, start_index + page_size);
};

const build_pagination_request = (headers, url, base_params, extract_payload) =>
  axios.get(url, {
    headers,
    params: {
      ...base_params,
      page_number: 1,
      page_size: DEFAULT_PAGE_SIZE,
    },
  }).then((response) => extract_payload(response));

const build_follow_up_request = (
  headers,
  url,
  base_params,
  page_number,
  extract_payload
) =>
  axios.get(url, {
    headers,
    params: {
      ...base_params,
      page_number,
      page_size: DEFAULT_PAGE_SIZE,
    },
  }).then((response) => extract_payload(response));

export const fetch_all_template_pages = async ({
  headers,
  url,
  base_params,
  extract_payload,
}) => {
  const first_page = await build_pagination_request(
    headers,
    url,
    base_params,
    extract_payload
  );
  const next_pages = build_page_numbers(first_page.total_pages);
  if (!next_pages.length) {
    return first_page.rows;
  }
  const responses = await Promise.all(
    next_pages.map((page_number) =>
      build_follow_up_request(headers, url, base_params, page_number, extract_payload)
    )
  );
  return [first_page, ...responses].flatMap((payload) => payload.rows);
};

export const extract_standard_template_payload = (response) => ({
  rows: response?.data?.templates || [],
  total_pages: response?.data?.total_pages || 1,
});

export const extract_pdf_template_payload = (response) => ({
  rows: response?.data?.data || [],
  total_pages: response?.data?.pagination?.total_pages || 1,
});
